import { createAsyncThunk } from '@reduxjs/toolkit';
import { createApi } from '@reduxjs/toolkit/query/react';
import { AxiosProgressEvent } from 'axios';

import { apiSahSupplierInvoices } from '~constants/api';

import { rtkBaseQueryWrapper, unwrap } from '~libs/axiosWrapper';
import { download, downloadFile, get, post, put, remove, upload } from '~libs/genericAction';
import { createThunks, Params } from '~libs/reduxUtils';

import { AnalyseInvoiceResponse, SupplierInvoiceActivityLog } from '~support-at-home/models';

export const typePrefix = `supplier-invoices`;

interface DownloadParams extends Params {
  onProgress: (value: string) => void;
}
const config = [
  { actionType: 'getAll', apiPath: apiSahSupplierInvoices, method: get },

  { actionType: 'createInvoice', apiPath: apiSahSupplierInvoices, method: post },
  { actionType: 'getEstimate', apiPath: `${apiSahSupplierInvoices}/estimate`, method: get },
  { actionType: 'getOne', apiPath: (params: Params) => `${apiSahSupplierInvoices}/${params.id}`, method: get },
  {
    actionType: 'getFile',
    apiPath: (params: Params) => `${apiSahSupplierInvoices}/files/${params.id}`,
    method: downloadFile,
  },
  {
    actionType: 'deleteFile',
    apiPath: (params: Params) => `${apiSahSupplierInvoices}/files/${params.id}`,
    method: remove,
  },
  { actionType: 'updateInvoice', apiPath: (params: Params) => `${apiSahSupplierInvoices}/${params.id}`, method: put },
  {
    actionType: 'deleteInvoice',
    apiPath: (params: Params) => `${apiSahSupplierInvoices}/${params.id}`,
    method: remove,
  },
  {
    actionType: 'updateStatus',
    apiPath: (params: Params) => `${apiSahSupplierInvoices}/${params.id}/update-status`,
    method: post,
  },
];

type UploadParams = {
  id: string;
  file: File;
  onProgress?: (progress: string | number) => void;
  additionalData?: Record<string, any>;
};

export const uploadInvoiceFile = createAsyncThunk(
  `${typePrefix}/uploadFile`,
  async ({ id, file, onProgress, additionalData }: UploadParams, { rejectWithValue }) => {
    try {
      return upload(`${apiSahSupplierInvoices}/${id}/files`, file, onProgress, additionalData);
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  },
);
export const exportAll = async ({ onProgress, params }: DownloadParams) =>
  download(`${apiSahSupplierInvoices}/export`, params, onProgress);

interface SupplierInvoiceUploadResponse {
  message?: string;
}

export const supplierInvoicesApi = createApi({
  reducerPath: 'supplierInvoicesApi',
  baseQuery: rtkBaseQueryWrapper({ baseUrl: apiSahSupplierInvoices }),
  tagTypes: ['supplier-invoices', 'supplier-invoice-logs'],
  endpoints: (builder) => ({
    supplierInvoiceUpload: builder.mutation<
      SupplierInvoiceUploadResponse,
      { formData: FormData; onProgress?: (prog: number) => void }
    >({
      query: ({ formData, onProgress }) => ({
        url: '/import',
        method: 'POST',
        body: formData,
        onUploadProgress: (evt: AxiosProgressEvent) => {
          if (onProgress && evt.total) {
            const percent = Math.round((evt.loaded / evt.total) * 100);
            onProgress(percent);
          }
        },
      }),
      invalidatesTags: (_result, _error) => [{ type: 'supplier-invoices', id: 'LIST' }],
      transformResponse: (payload) => unwrap<SupplierInvoiceUploadResponse>(payload),
    }),
    getInvoiceLogs: builder.query<SupplierInvoiceActivityLog[], { id: string }>({
      query: ({ id }) => ({ url: `/${id}/logs`, method: 'GET' }),
      providesTags: (_result, _error, { id }) => [{ type: 'supplier-invoice-logs', id }],
      transformResponse: (payload) => unwrap<SupplierInvoiceActivityLog[]>(payload),
    }),
    analyseInvoiceFile: builder.mutation<AnalyseInvoiceResponse, { fileId: string }>({
      query: ({ fileId }) => ({
        url: `/files/${fileId}/analyse`,
        method: 'POST',
      }),
      transformResponse: (payload) => unwrap<AnalyseInvoiceResponse>(payload),
    }),
    analyseUploadedFile: builder.mutation<AnalyseInvoiceResponse, { file: File }>({
      query: ({ file }) => {
        const formData = new FormData();
        formData.append('file', file);
        return {
          url: '/analyse',
          method: 'POST',
          body: formData,
        };
      },
      transformResponse: (payload) => unwrap<AnalyseInvoiceResponse>(payload),
    }),
  }),
});

export const {
  useSupplierInvoiceUploadMutation,
  useGetInvoiceLogsQuery,
  useAnalyseInvoiceFileMutation,
  useAnalyseUploadedFileMutation,
} = supplierInvoicesApi;

export const {
  getAll,
  createInvoice,
  updateInvoice,
  deleteInvoice,
  getFile,
  getEstimate,
  getOne,
  updateStatus,
  deleteFile,
} = createThunks(typePrefix, config);
